import { Formik } from 'formik';
import { Fragment, useCallback } from 'react';

import { ButtonColors } from 'types';
import { Flex } from 'components/flex';
import { Icon } from 'components/icon';
import { longRange } from 'utils/time-off';
import { InputField } from 'components/fields';
import { decisionToast } from 'utils/time-off';
import * as CommonStyles from 'components/styles';
import { Dialog, DialogType } from 'components/dialog';
import { DeleteDialog } from 'components/delete-dialog';
import { isMobile, useAlert, handleMutation } from 'hooks';
import { Button, ButtonTypes, ButtonWithRef } from 'components/button';
import {
  useDecideTimeOffMutation,
  useDeleteTimeOffMutation,
} from 'store/api/time-off';

import { SomeDaysDialog } from './some-days';
import { showsText, declineSchema } from './config';
import { EditDecisionDialog } from './edit-decision';
import {
  Decide,
  TimeOffDecisionDialogProps,
  TimeOffApprovalDialogProps,
} from './types';

// TIME OFF (owner 24 Oct 2026; plan §6.2): the reimbursement three icons
// (ApprovalPayoutDialog) for a time-off request, plus Delete and Edit
// decision. Every icon is drawn only when the server's `can` flag says so,
// so an approver's own request never shows any.

const ApproveDialog = ({ row, decide }: TimeOffDecisionDialogProps) => {
  const mobile = isMobile();
  const shows = showsText(row);

  return (
    <Dialog
      confirmText="Approve"
      title="Approve time off"
      closeOnDocumentClick={false}
      confirmStyle={ButtonColors.Green}
      onConfirm={() => decide({ decision: 'approved' }, 'approved')}
      trigger={
        <ButtonWithRef
          tooltip="Approve"
          fullWidth={mobile}
          icon={<Icon.CheckCircle />}
          colorType={ButtonColors.LightGreen}
        />
      }
      text={
        <>
          You're going to approve <span className="semibold">{row.name}</span>'s
          time off{' '}
          <span className="semibold">
            {longRange(row.startDate, row.endDate)}
          </span>{' '}
          ({row.days === 1 ? '1 day' : `${row.days} days`}).
          {shows && (
            <>
              {' '}
              {shows.name} is on{' '}
              {shows.first.map((show, index) => (
                <Fragment key={`${show.name}-${index}`}>
                  {index > 0 && ' and '}
                  <span className="semibold">{show.name}</span> on{' '}
                  <span className="semibold">{show.day}</span>
                </Fragment>
              ))}
              {shows.more > 0 &&
                ` and ${shows.more} more ${shows.more === 1 ? 'show' : 'shows'}`}
              . Approving doesn't remove anyone from the show. If {shows.name}{' '}
              won't work it, take {shows.name} off the team in Assign team.
            </>
          )}
        </>
      }
    />
  );
};

const DeclineDialog = ({
  decide,
}: Pick<TimeOffDecisionDialogProps, 'decide'>) => {
  const mobile = isMobile();

  return (
    <Dialog
      title="Decline time off"
      closeOnDocumentClick={false}
      trigger={
        <ButtonWithRef
          tooltip="Decline"
          fullWidth={mobile}
          colorType={ButtonColors.LightRed}
          icon={<Icon.CloseCircleNotFilled />}
        />
      }
    >
      {(close, _, ButtonWrap) => (
        <Formik
          initialValues={{ note: '' }}
          validationSchema={declineSchema}
          onSubmit={async ({ note }) => {
            const result = await decide(
              { note: note.trim(), decision: 'notApproved' },
              'notApproved'
            );
            if (result) {
              close();
            }
          }}
        >
          {({ dirty, isValid, isSubmitting }) => (
            <CommonStyles.Form gap={24}>
              <InputField
                name="note"
                label="Reason"
                type="textarea"
                controlErrorLabel
                placeholder="Add a reason"
              />
              <p>Required, 5–200 characters. The rep sees this.</p>

              <ButtonWrap type={DialogType.Plain}>
                <Button
                  fullWidth
                  text="Cancel"
                  onClick={close}
                  colorType={ButtonColors.LightBlue}
                />
                <Button
                  fullWidth
                  text="Decline"
                  type={ButtonTypes.Submit}
                  colorType={ButtonColors.LightRed}
                  disabled={!dirty || !isValid || isSubmitting}
                />
              </ButtonWrap>
            </CommonStyles.Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

export const TimeOffApprovalDialog = ({ row }: TimeOffApprovalDialogProps) => {
  const { showSuccessAlert } = useAlert();
  const [decideTimeOff, decideMutation] = useDecideTimeOffMutation();
  const [deleteTimeOff, deleteMutation] = useDeleteTimeOffMutation();

  // Errors (409 "Someone else just changed this request", 400 …) are the
  // server's words, shown by handleMutation as for every other action.
  handleMutation({ ...decideMutation });

  const decide: Decide = useCallback(
    async (request, kind) => {
      try {
        const result = await decideTimeOff({
          ...request,
          id: row.id,
          version: row.version,
        }).unwrap();
        showSuccessAlert(decisionToast(kind, result.sms, result.repName));
        return result;
      } catch {
        return null;
      }
    },
    [row.id, row.version]
  );

  const { can } = row;

  return (
    <Flex gap={8}>
      {can.approve && <ApproveDialog row={row} decide={decide} />}
      {can.approveSomeDays && <SomeDaysDialog row={row} decide={decide} />}
      {can.decline && <DeclineDialog decide={decide} />}
      {can.editDecision && <EditDecisionDialog row={row} decide={decide} />}
      {can.delete && (
        <DeleteDialog
          id={row.id}
          confirmText="Delete"
          name={row.name || ''}
          mutation={deleteMutation}
          title="Delete time off request"
          action={() => deleteTimeOff(row.id)}
          successMessage="Time off request deleted"
          text={
            <>
              You are about to delete{' '}
              <span className="semibold">{row.name}</span>'s request for{' '}
              <span className="semibold">
                {longRange(row.startDate, row.endDate, { dropYear: true })}
              </span>
              . Are you sure?
            </>
          }
        />
      )}
    </Flex>
  );
};
